import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight, Play, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { api, cfmt, pfmt } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import JsonLd, { isoDuration } from '../components/common/JsonLd';
import NewsletterForm from '../components/common/NewsletterForm';
import NotFound from './NotFound';


export default function VideoDetail() {
    const { slug } = useParams();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['video', slug],
        queryFn: async () => (await api.get(`/videos/${slug}`)).data,
        retry: false,
    });
    const [expandTranscript, setExpandTranscript] = useState(false);

    if (isLoading) return <div className="min-h-[60vh] bg-black" />;
    if (isError || !data) return <NotFound />;

    const v = data.video;
    const related = data.related || {};
    const siblings = data.siblings || [];

    // Structured data — VideoObject + optional FAQPage. Emitted for Google Video
    // rich results and FAQ rich results. See schema.org/VideoObject and /FAQPage.
    const canonical = `https://bestneighbourhoods.ca/videos/${slug}`;
    const videoObject = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: v.title,
        description: v.meta_description || v.dek || v.title,
        thumbnailUrl: v.thumbnail ? [v.thumbnail] : undefined,
        uploadDate: v.published_at || v.created_at,
        duration: isoDuration(v.duration_sec),
        contentUrl: v.video_url || undefined,
        embedUrl: v.youtube_id ? `https://www.youtube.com/embed/${v.youtube_id}` : undefined,
        url: canonical,
        publisher: {
            '@type': 'Organization',
            name: 'BestNeighbourhoods.ca',
            logo: {
                '@type': 'ImageObject',
                url: 'https://bestneighbourhoods.ca/og-default.jpg',
            },
        },
        inLanguage: 'en-CA',
        isFamilyFriendly: true,
    };
    const faqPage = v.faqs?.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: v.faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
    } : null;

    return (
        <div className="bg-white">
            <SEOHead
                title={v.title}
                description={v.meta_description || v.dek}
                path={`/videos/${slug}`}
                image={v.thumbnail}
                type="video.other"
            />
            <JsonLd data={[videoObject, faqPage]} />

            {/* ==================== PLAYER ==================== */}
            <section className="bg-black">
                <div className="max-w-[1400px] mx-auto">
                    <div className="relative w-full bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="video-player">
                        {v.youtube_id ? (
                            <iframe
                                className="absolute inset-0 w-full h-full"
                                src={`https://www.youtube.com/embed/${v.youtube_id}?rel=0&modestbranding=1`}
                                title={v.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : v.video_url ? (
                            <video
                                src={v.video_url}
                                controls
                                poster={v.thumbnail}
                                playsInline
                                className="absolute inset-0 w-full h-full bg-black"
                            />
                        ) : (
                            <div className="absolute inset-0">
                                {v.thumbnail && (
                                    <img src={v.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" aria-hidden="true" />
                                )}
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                                    <div className="font-editorial text-white text-[22px] tracking-editorial">Video coming soon</div>
                                    <div className="mt-2 text-white/60 text-[13px] max-w-md">
                                        The full article, transcript and FAQs below cover everything in this piece.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ==================== HEADER META ==================== */}
            <section className="border-b border-neutral-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                            <Link to={`/videos?category=${v.category}`} className="hover:text-black">
                                {v.category?.replace(/-/g, ' ')}
                            </Link>
                            <span className="text-neutral-300">·</span>
                            <span>{v.published_at}</span>
                        </div>
                        <h1
                            className="font-editorial mt-6 leading-[1.03] text-black"
                            style={{ fontSize: 'clamp(36px, 5vw, 68px)', letterSpacing: '-0.02em' }}
                            data-testid="video-title"
                        >
                            {v.title}
                        </h1>
                        {v.dek && (
                            <p className="mt-5 font-editorial italic text-neutral-600 max-w-3xl" style={{ fontSize: '21px', lineHeight: 1.45 }}>
                                {v.dek}
                            </p>
                        )}
                    </motion.div>
                </div>
            </section>

            {/* ==================== BODY GRID ==================== */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Article + transcript + FAQ */}
                    <div className="lg:col-span-8 space-y-16">
                        {v.article_md && (
                            <article className="prose prose-neutral max-w-none" data-testid="video-article">
                                <MarkdownLite md={v.article_md} />
                            </article>
                        )}

                        {/* FAQs */}
                        {v.faqs?.length > 0 && (
                            <div data-testid="video-faqs">
                                <div className="overline">Frequently asked</div>
                                <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: '32px', letterSpacing: '-0.02em' }}>
                                    Answers, not opinions.
                                </h2>
                                <div className="mt-8 border-t border-neutral-200">
                                    {v.faqs.map((f, i) => (
                                        <FaqRow key={i} q={f.q} a={f.a} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Transcript */}
                        {v.transcript && (
                            <div data-testid="video-transcript">
                                <button
                                    onClick={() => setExpandTranscript(!expandTranscript)}
                                    className="flex items-center gap-2 text-[13px] tracking-[0.18em] uppercase text-neutral-500 hover:text-black font-mono-data"
                                >
                                    <ChevronRight className={`w-4 h-4 transition-transform ${expandTranscript ? 'rotate-90' : ''}`} />
                                    {expandTranscript ? 'Hide' : 'Read'} full transcript
                                </button>
                                {expandTranscript && (
                                    <div className="mt-6 border-t border-neutral-200 pt-6 whitespace-pre-line text-[14.5px] text-neutral-700 leading-relaxed font-editorial">
                                        {v.transcript}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar — related everything */}
                    <aside className="lg:col-span-4 space-y-10">
                        {/* Related neighbourhoods */}
                        {related.neighbourhoods?.length > 0 && (
                            <div data-testid="video-related-hoods">
                                <div className="overline">Neighbourhoods in this video</div>
                                <div className="mt-4 border-t border-neutral-200">
                                    {related.neighbourhoods.map((n) => (
                                        <Link key={n.slug} to={`/neighbourhoods/${n.slug}`} className="block border-b border-neutral-200 py-3 group hover:bg-[#FAFAF8]">
                                            <div className="flex items-baseline justify-between gap-3">
                                                <div className="font-editorial text-[18px] tracking-editorial group-hover:italic">{n.name}</div>
                                                <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-black" />
                                            </div>
                                            <div className="mt-1 text-[11px] tracking-[0.16em] uppercase text-neutral-500 font-mono-data">
                                                {n.city} · {cfmt(n.median_price)} · +{pfmt(n.forecast_5yr_pct)} 5Y
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Related reports */}
                        {related.reports?.length > 0 && (
                            <div data-testid="video-related-reports">
                                <div className="overline">Related reports</div>
                                <ul className="mt-4">
                                    {related.reports.map((r, i) => (
                                        <li key={i} className="border-b border-neutral-200 py-3">
                                            <Link to={r.url} className="flex items-baseline justify-between gap-3 group">
                                                <span className="font-editorial text-[16px] leading-snug group-hover:italic">{r.title}</span>
                                                <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-black shrink-0" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Related calculators */}
                        {related.calculators?.length > 0 && (
                            <div data-testid="video-related-calcs">
                                <div className="overline">Run the numbers</div>
                                <ul className="mt-4">
                                    {related.calculators.map((c) => (
                                        <li key={c.slug} className="border-b border-neutral-200 py-3">
                                            <Link to={c.url} className="flex items-baseline justify-between gap-3 group">
                                                <span className="font-editorial text-[16px] leading-snug group-hover:italic">{c.label}</span>
                                                <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-black" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Newsletter CTA */}
                        <div className="bg-[#FAFAF8] border border-neutral-200 p-6" data-testid="video-newsletter-cta">
                            <div className="overline">The Sunday briefing</div>
                            <h3 className="font-editorial mt-3 leading-tight" style={{ fontSize: '22px' }}>
                                One neighbourhood. One number. One insight.
                            </h3>
                            <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed">
                                Every Sunday morning. Free. Signal only.
                            </p>
                            <div className="mt-4">
                                <NewsletterForm source={`video:${v.slug}`} tags={[`video`, `category:${v.category}`]} />
                            </div>
                        </div>

                        {/* Strategy session CTA */}
                        <Link
                            to="/strategy-session"
                            data-testid="video-strategy-cta"
                            className="block bg-brand-navy text-white p-6 hover:bg-neutral-800 transition-colors"
                           
                        >
                            <div className="text-[10px] tracking-[0.24em] uppercase text-white/60 font-mono-data">
                                Book a Strategy Session
                            </div>
                            <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '22px' }}>
                                Turn the video into a decision.
                            </div>
                            <div className="mt-4 flex items-center gap-2 text-[12.5px]">
                                Thirty minutes with an independent analyst
                                <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </Link>
                    </aside>
                </div>
            </section>

            {/* ==================== KEEP WATCHING ==================== */}
            {siblings.length > 0 && (
                <section className="bg-[#0A0A0A] text-white">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                        <div className="flex items-baseline justify-between">
                            <div>
                                <div className="overline text-white/60">Keep watching</div>
                                <h2 className="font-editorial mt-3 tracking-editorial text-white leading-tight" style={{ fontSize: '32px', letterSpacing: '-0.01em' }}>
                                    More in {v.category?.replace(/-/g, ' ')}.
                                </h2>
                            </div>
                            <Link to={`/videos?category=${v.category}`} className="text-[13.5px] tracking-[0.16em] uppercase text-white/60 hover:text-white font-mono-data flex items-center gap-1">
                                Browse all <ArrowUpRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {siblings.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/videos/${s.slug}`}
                                    data-testid={`video-sibling-${s.slug}`}
                                    className="group block relative overflow-hidden bg-neutral-900 border border-white/10 hover:border-white/40 transition-colors"
                                >
                                    <div className="relative aspect-video bg-neutral-800">
                                        <img src={s.thumbnail} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                                        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.75) 100%)' }} />
                                        <Play className="absolute top-3 right-3 w-4 h-4 text-white/80" />
                                        <div className="absolute bottom-3 left-3 right-3 font-editorial text-white leading-tight line-clamp-2" style={{ fontSize: '17px' }}>
                                            {s.title}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}


function FaqRow({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-neutral-200">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-start justify-between gap-4 py-5 text-left"
            >
                <span className="font-editorial text-[19px] leading-snug tracking-editorial">{q}</span>
                <ChevronRight className={`w-5 h-5 mt-1 text-neutral-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
            </button>
            {open && (
                <div className="pb-5 text-[14.5px] text-neutral-700 leading-relaxed">
                    {a}
                </div>
            )}
        </div>
    );
}


/**
 * Deliberately minimal markdown renderer — headings, paragraphs, lists, links.
 * Avoids pulling in a heavy library for a handful of AI-authored articles.
 */
function MarkdownLite({ md }) {
    const blocks = md.trim().split(/\n\n+/);
    return (
        <div className="space-y-6">
            {blocks.map((b, i) => {
                if (b.startsWith('# ')) {
                    return <h1 key={i} className="font-editorial text-[42px] tracking-editorial leading-tight">{b.slice(2)}</h1>;
                }
                if (b.startsWith('## ')) {
                    return <h2 key={i} className="font-editorial text-[26px] tracking-editorial leading-tight mt-6">{b.slice(3)}</h2>;
                }
                if (b.startsWith('### ')) {
                    return <h3 key={i} className="font-editorial text-[20px] tracking-editorial leading-tight">{b.slice(4)}</h3>;
                }
                // list block (ordered or unordered)
                if (/^\s*[-*]\s+/m.test(b) || /^\s*\d+\.\s+/m.test(b)) {
                    const items = b.split('\n').map((line) => line.replace(/^\s*(?:[-*]|\d+\.)\s+/, ''));
                    return (
                        <ol key={i} className="list-decimal list-outside pl-6 space-y-2 text-[15.5px] leading-relaxed text-neutral-800">
                            {items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: renderInline(it) }} />)}
                        </ol>
                    );
                }
                return <p key={i} className="text-[16px] leading-relaxed text-neutral-800" dangerouslySetInnerHTML={{ __html: renderInline(b) }} />;
            })}
        </div>
    );
}

function renderInline(s) {
    return s
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline decoration-neutral-300 underline-offset-4 hover:decoration-black">$1</a>');
}
